import { Checkbox } from "@/components/ui/checkbox"
import {
  useTranslation,
  type TranslationKeys,
} from "@/hooks/useTranslation/useTranslation"
import { useEffect, useRef, useState, type ReactNode } from "react"
import { EntityListRow } from "../shared/EntityListRow"
import {
  MasterDataFilterChips,
  MasterDataFilterToggle,
  useMasterDataFiltersState,
  type MasterDataFacet,
} from "./MasterDataFilters"
import { MasterDataList } from "./MasterDataList"
import { MasterDataListMeta } from "./MasterDataListMeta"
import { MasterDataSearch } from "./MasterDataSearch"
import { MasterDataSort, type MasterDataSortValue } from "./MasterDataSort"

/**
 * `useInfiniteQuery`'s `keepPreviousData` keeps showing the old rows (with
 * `isLoading` false, only `isFetching` true) while a new sort's page loads —
 * good for search/filter typing, but a sort change reads as "nothing
 * happened" without a skeleton. Tracks the transition and stays "loading"
 * from the moment `sort` changes until that sort's fetch settles.
 */
function useSortChangeLoading(
  sort: MasterDataSortValue | undefined,
  isFetching: boolean,
): boolean {
  const [pending, setPending] = useState(false)
  const prevSort = useRef(sort)

  useEffect(() => {
    if (prevSort.current !== sort) {
      prevSort.current = sort
      setPending(true)
    }
  }, [sort])

  useEffect(() => {
    if (pending && !isFetching) setPending(false)
  }, [pending, isFetching])

  return pending
}

/**
 * Shared shell behind the five Master Data list panels (Articles, Products,
 * Suppliers, Customers, Substances) — search + facet filters + infinite list
 * + row selection, with the row's own content and its filter set as config.
 * Bulk multi-select (`selectedIds`/`onToggleSelected`) is optional since the
 * Substances panel has no bulk actions and renders no checkbox.
 */
export function EntityListPanel<TRow extends { id: string }>({
  rows,
  isLoading,
  isFetching,
  hasNextPage,
  isFetchingNextPage,
  onFetchMore,
  selectedId,
  onSelectRow,
  selectedIds,
  onToggleSelected,
  hasFlag,
  search,
  onSearchChange,
  searchEntityLabelKey,
  facets,
  onClearAllFilters,
  renderContent,
  totalCount,
  sort,
  onSortChange,
  agentTarget,
  filterTargetKey,
  tabs,
}: {
  rows: TRow[]
  isLoading: boolean
  isFetching: boolean
  hasNextPage?: boolean
  isFetchingNextPage?: boolean
  onFetchMore?: () => void
  selectedId: string | null
  onSelectRow: (id: string) => void
  selectedIds?: Set<string>
  onToggleSelected?: (id: string) => void
  hasFlag?: (row: TRow) => boolean
  search: string
  onSearchChange: (value: string) => void
  searchEntityLabelKey: TranslationKeys
  facets: MasterDataFacet[]
  onClearAllFilters: () => void
  renderContent: (row: TRow) => ReactNode
  /** Total row count from the paginated API response; omit to hide the count/sort meta row. */
  totalCount?: number
  /** Current sort selection; omit (with onSortChange) to hide the sort control. */
  sort?: MasterDataSortValue
  onSortChange?: (value: MasterDataSortValue) => void
  /** Agent-addressable target key (see `agentTargets.ts`), marked on the panel root. */
  agentTarget?: string
  /** Agent target key for the filter control, e.g. "suppliers.filters". */
  filterTargetKey?: string
  /** Optional mode-switch tabs (e.g. `FilterTabs`), rendered between the search header and the row list. Omit to render nothing. */
  tabs?: ReactNode
}) {
  const { translate } = useTranslation()
  const {
    isOpen: filtersOpen,
    setIsOpen: setFiltersOpen,
    hasActiveFilters,
  } = useMasterDataFiltersState(facets)
  const sortChanging = useSortChangeLoading(sort, isFetching)
  return (
    <div data-agent-target={agentTarget} className="flex h-full flex-col">
      <MasterDataSearch
        value={search}
        onChange={onSearchChange}
        loading={!!search && isFetching}
        placeholder={translate("MC_MD_SEARCH_PLACEHOLDER", {
          entity: translate(searchEntityLabelKey),
        })}
        sort={
          sort !== undefined &&
          onSortChange && (
            <MasterDataSort value={sort} onChange={onSortChange} />
          )
        }
        filterToggle={
          facets.length > 0 && (
            <MasterDataFilterToggle
              isOpen={filtersOpen}
              onToggle={() => setFiltersOpen((current) => !current)}
              hasActiveFilters={hasActiveFilters}
              agentTarget={filterTargetKey}
            />
          )
        }
        filterChips={
          filtersOpen &&
          facets.length > 0 && (
            <MasterDataFilterChips
              facets={facets}
              hasActiveFilters={hasActiveFilters}
              onClearAll={onClearAllFilters}
            />
          )
        }
        meta={
          totalCount !== undefined &&
          !!selectedIds?.size && (
            <MasterDataListMeta
              selectedCount={selectedIds.size}
              totalCount={totalCount}
            />
          )
        }
      />
      {tabs}
      <MasterDataList
        rows={rows}
        isLoading={isLoading || sortChanging}
        hasNextPage={hasNextPage}
        isFetchingNextPage={isFetchingNextPage}
        onFetchMore={onFetchMore}
        selectedKey={selectedId ?? undefined}
        totalCount={totalCount}
        renderRow={(row) => (
          <EntityListRow
            key={row.id}
            rowId={row.id}
            isSelected={row.id === selectedId}
            hasFlag={hasFlag?.(row)}
            onClick={() => {
              onSelectRow(row.id)
              if (onToggleSelected && selectedIds && selectedIds.size > 0) {
                onToggleSelected(row.id)
              }
            }}
            checkbox={
              onToggleSelected && selectedIds ? (
                <Checkbox
                  checked={selectedIds.has(row.id)}
                  onCheckedChange={() => onToggleSelected(row.id)}
                />
              ) : undefined
            }
          >
            {renderContent(row)}
          </EntityListRow>
        )}
      />
    </div>
  )
}
